import React from "react";
import cx from "classnames";
import pluralize from "pluralize";
import _ from "lodash";

import { ScrollableContainer } from "src/components/scroller";
import { FileService } from "src/utils/file.service";
import { Simulation } from "src/types/coaching/simulation";

import styles from "./metrics.module.scss";

export interface MetricsProps {
  name: string;
  value?: React.ReactNode;
  subTitle?: string;
}

export const Metrics: React.FC<MetricsProps> = ({ name, value, subTitle }) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.metricName}>{name}</span>
        <span className={styles.metricValue}>{value}</span>
        {subTitle != null && (
          <span className={styles.metricSubTitle}>{subTitle}</span>
        )}
      </div>
    </div>
  );
};

const LeftArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.leftArrow)}>
    <i className="paperflite-arrow-up"></i>
  </div>
);

const RightArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.rightArrow)}>
    <i className="paperflite-arrow-up"></i>
  </div>
);

const getCompletionInfo = (completed: number, total: number) => {
  const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const text = `${completed} of ${total} learners have completed`;
  return { rate, text };
};

const getAvgScoreMetric = (
  averageScore: number,
  completedLearnerAttempts: number,
) => ({
  value:
    completedLearnerAttempts > 0
      ? `${Math.round(averageScore * 100)}%`
      : "na",
  subTitle:
    completedLearnerAttempts > 0
      ? `across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`
      : "No learners have taken the learning yet",
});

export interface SimulationMetricsCarouselProps {
  simulation: Simulation;
}

export const SimulationMetricsCarousel: React.FC<
  SimulationMetricsCarouselProps
> = ({ simulation }) => {
  const averageScore = _.get(simulation, "averageScore", 0) as number;
  const completedLearners = _.get(simulation, "completedLearners", 0) as number;
  const completedLearnerAttempts = _.get(
    simulation,
    "completedLearnerAttempts",
    0,
  ) as number;
  const totalLearners = _.get(simulation, "totalLearners", 0) as number;
  const averageTimeSpent = _.get(simulation, "averageTimeSpent", 0) as number;

  const completionInfo = getCompletionInfo(completedLearners, totalLearners);
  const avgScoreMetric = getAvgScoreMetric(
    averageScore,
    completedLearnerAttempts,
  );

  return (
    <ScrollableContainer
      distance={300}
      leftArrowComponent={LeftArrow}
      rightArrowComponent={RightArrow}
    >
      <div className={styles.infoContainer}>
        <Metrics
          name="completion rate"
          value={`${completionInfo.rate}%`}
          subTitle={completionInfo.text}
        />
        <Metrics
          name="avg score"
          value={avgScoreMetric.value}
          subTitle={avgScoreMetric.subTitle}
        />
        <Metrics
          name="avg time spent"
          value={FileService.formatDuration(averageTimeSpent, "short")}
        />
      </div>
    </ScrollableContainer>
  );
};
